import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { IRootReducerState } from "../store/IRootReducer";
import { DEFAULT_PRODUCT, type ProductKey } from "../../shared/products/keys";

/**
 * Which products this instance has and which the caller holds a role in (spec §3, §4.5), from
 * `capabilities` as resolve-instance filled them. Not `ready` until that answer arrived: the route
 * guard waits on it rather than redirecting mid-load (advisor UAT bug, spec §6).
 *
 * A UI hint only: it decides what to show, never what the caller may do. The server gates every
 * product route itself.
 */
export function useEnabledProducts() {
  const capabilities = useSelector((state: IRootReducerState) => state.authReducer.capabilities);
  const isAuthenticated = useSelector(
    (state: IRootReducerState) => state.authReducer.isAuthenticated,
  );
  // With nobody signed in, getUserAccess returns before applyPermissions and no answer will
  // arrive; waiting would leave HomeRoute on a spinner where it renders today.
  const ready = capabilities.productsResolved || !isAuthenticated;
  // An empty resolved list is an older server or the full-access fallback: hire, as it always was.
  const products: ProductKey[] = useMemo(
    () => (ready && capabilities.products.length === 0 ? [DEFAULT_PRODUCT] : capabilities.products),
    [ready, capabilities.products],
  );
  return {
    products,
    productRoles: capabilities.productRoles,
    ready,
    has: (key: ProductKey) => products.includes(key),
    holdsRole: (key: ProductKey) => Boolean(capabilities.productRoles[key]),
  };
}
